const HFG_CART_SYNC_FLAG = '__hfgCartStateSyncStarted';

if (!window[HFG_CART_SYNC_FLAG]) {
  window[HFG_CART_SYNC_FLAG] = true;

  let syncSequence = 0;
  const charityNames = new Map();

  const cartLike = (value) => {
    return value && typeof value === 'object' && Array.isArray(value.items) && Number.isFinite(Number(value.item_count));
  };

  const fetchCart = async () => {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Cart state unavailable');
    return response.json();
  };

  const charityHandleFromCart = (cart) => {
    const params = new URLSearchParams(window.location.search);
    const queryHandle = String(params.get('supported_charity') || '').trim().toLowerCase();
    if (queryHandle) return queryHandle;

    const attributeHandle = String(cart?.attributes?.supported_charity || '').trim().toLowerCase();
    if (attributeHandle) return attributeHandle;

    for (const item of cart?.items || []) {
      const lineHandle = String(item?.properties?.supported_charity || '').trim().toLowerCase();
      if (lineHandle) return lineHandle;
    }
    return '';
  };

  const fallbackCharityName = (handle) => {
    return String(handle || '')
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const matchingShell = (handle) => {
    return Array.from(document.querySelectorAll('[data-hfg-charity-handle]')).find((node) => {
      return String(node.getAttribute('data-hfg-charity-handle') || '').trim().toLowerCase() === handle;
    });
  };

  const charityNameFromShell = (handle) => {
    const shell = matchingShell(handle);
    const node = shell?.querySelector('[data-role="charityShellName"], [data-hfg-client-charity-name]');
    return String(node?.textContent || '').trim();
  };

  const fetchCharityName = async (handle) => {
    if (!handle) return '';
    const shellName = charityNameFromShell(handle);
    if (shellName) return shellName;
    if (charityNames.has(handle)) return charityNames.get(handle);

    const request = (async () => {
      const endpoint = new URL('/apps/charity-portal/access-check/', window.location.origin);
      endpoint.searchParams.set('slug', handle);
      const cpToken = new URLSearchParams(window.location.search).get('cp_token');
      if (cpToken) endpoint.searchParams.set('cp_token', cpToken);

      try {
        const response = await fetch(endpoint.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        const payload = response.ok ? await response.json() : null;
        return String(payload?.branding?.name || '').trim() || fallbackCharityName(handle);
      } catch (_error) {
        return fallbackCharityName(handle);
      }
    })();

    charityNames.set(handle, request);
    return request;
  };

  const estimateDonation = (cart, node) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const percentage = Number(node?.dataset?.hfgPercentage);
    const country = String(node?.dataset?.hfgCountry || '').toUpperCase();
    const taxesIncluded = node?.dataset?.hfgTaxesIncluded === 'true';
    // Australian list prices include GST. Project included GST for the cart by
    // taxable line; the completed order's Shopify tax lines control settlement.
    const estimateAustralianGst = country === 'AU';
    const currency = String(cart?.currency || '').toUpperCase();
    if (!items.length || !Number.isFinite(percentage) || percentage <= 0 || !/^[A-Z]{3}$/.test(currency)) return null;
    if (taxesIncluded && !estimateAustralianGst) return null;

    const eligible = items.filter((item) => !item.gift_card);
    if (!eligible.length || eligible.some((item) => !Number.isInteger(item.final_line_price) || item.final_line_price < 0)) return null;
    const allLinesTotal = items.reduce((sum, item) => sum + Number(item.final_line_price), 0);
    const cartTotal = Number(cart.total_price);
    if (!Number.isInteger(allLinesTotal) || !Number.isInteger(cartTotal) || cartTotal < 0 || cartTotal > allLinesTotal) return null;

    const cartDiscount = allLinesTotal - cartTotal;
    let netProductCents;
    if (cartDiscount > 0) {
      // Shopify does not expose the allocation of a cart-wide discount to mixed lines here.
      if (eligible.length !== items.length || eligible.some((item) => item.taxable !== eligible[0].taxable)) return null;
      netProductCents = estimateAustralianGst && eligible[0].taxable ? cartTotal - Math.round(cartTotal / 11) : cartTotal;
    } else {
      netProductCents = eligible.reduce((sum, item) => {
        const includedTax = estimateAustralianGst && item.taxable ? Math.round(item.final_line_price / 11) : 0;
        return sum + item.final_line_price - includedTax;
      }, 0);
    }

    const amountCents = Math.round(netProductCents * percentage / 100);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amountCents / 100);
    } catch (_error) {
      return null;
    }
  };

  const applyCartState = (cart, handle, charityName) => {
    const itemCount = Math.max(0, Number(cart?.item_count) || 0);

    requestAnimationFrame(() => {
      document.querySelectorAll('[data-hfg-cart-donation-estimate]').forEach((node) => {
        const estimate = estimateDonation(cart, node);
        node.textContent = estimate || 'Calculated at checkout';
      });
      document.querySelectorAll('[data-role="cartDonationCharityName"]').forEach((node) => {
        node.textContent = charityName || fallbackCharityName(handle);
      });
      document
        .querySelectorAll(
          '[data-hfg-charity-handle] [data-role="charityShellContextName"], [data-hfg-client-charity-name]'
        )
        .forEach((node) => {
          node.textContent = charityName || fallbackCharityName(handle);
        });
      document
        .querySelectorAll('.charity-shell-chrome__cart-count, .hfg-client-charity-shell__cart-count')
        .forEach((node) => {
          node.textContent = String(itemCount);
          const link = node.closest('a');
          if (link) link.setAttribute('aria-label', `Cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`);
        });
      document.querySelectorAll('[data-hfg-charity-support-trigger][data-hfg-charity-handle]').forEach((node) => {
        node.setAttribute('data-hfg-charity-handle', handle);
      });

      document.documentElement.dataset.hfgCartSyncState = 'ready';
      document.documentElement.dataset.hfgCartItemCount = String(itemCount);
      document.documentElement.dataset.hfgCartCharity = handle;
      document.dispatchEvent(
        new CustomEvent('hfg:cart-ui-synced', {
          detail: { handle, charityName, itemCount },
        })
      );
    });
  };

  const syncCartState = async (providedCart) => {
    const sequence = ++syncSequence;
    document.documentElement.dataset.hfgCartSyncState = 'loading';

    try {
      const cart = cartLike(providedCart) ? providedCart : await fetchCart();
      const handle = charityHandleFromCart(cart);
      const charityName = await fetchCharityName(handle);
      if (sequence !== syncSequence) return;
      applyCartState(cart, handle, charityName);
    } catch (_error) {
      if (sequence === syncSequence) document.documentElement.dataset.hfgCartSyncState = 'error';
    }
  };

  document.addEventListener('cart:update', (event) => {
    syncCartState(event?.detail?.resource);
  });
  document.addEventListener('hfg:cart-context-updated', () => syncCartState());
  window.addEventListener('pageshow', () => syncCartState());
  syncCartState();
}
